import useReveal from './useReveal'
import { tierOf, formatPct } from './territoryUtils'
import { conquestOnTrack, seedOf } from './battleGeometry'

// Capa de "conquista" del mapa en vivo, estilo frente de batalla. Por corredor:
//   - niebla: el corredor completo, tenue, lo que falta conquistar;
//   - territorio: polígono con bordes irregulares y rayado de carta militar que
//     avanza desde las ciudades hacia el medio según el % de dominación;
//   - frente: línea dentada donde termina la conquista, con espadas cruzadas;
//   - banderas en las ciudades tomadas. Dominada = todo el corredor tomado, sin frente.
// Las dominadas se dibujan al final para quedar arriba.

const Flag = ({ x, y, k }) => (
  <g transform={`translate(${x}, ${y}) scale(${k * 1.25})`} style={{ pointerEvents: 'none' }}>
    <ellipse cx={0} cy={0.6} rx={3.2} ry={1.3} fill='#000' opacity={0.28} />
    <line x1={0} y1={0} x2={0} y2={-18} stroke='var(--terr-pole)' strokeWidth={1.5} strokeLinecap='round' />
    <path className='live-flag' d='M0.7,-18 L13,-14.2 L0.7,-10.4 Z' fill='var(--terr-land)' stroke='var(--terr-edge)' strokeWidth={0.7} strokeLinejoin='round' />
  </g>
)

const Swords = ({ x, y, k }) => (
  <g transform={`translate(${x}, ${y}) scale(${k * 1.25})`} className='live-front-pulse' style={{ pointerEvents: 'none' }}>
    <circle r={8} fill='var(--panel)' stroke='var(--terr-front)' strokeWidth={1.2} />
    <g stroke='var(--terr-front)' strokeWidth={1.6} strokeLinecap='round'>
      <line x1={-4.2} y1={-4.2} x2={4} y2={4} />
      <line x1={4.2} y1={-4.2} x2={-4} y2={4} />
      <line x1={0.9} y1={3.7} x2={3.7} y2={0.9} />
      <line x1={-0.9} y1={3.7} x2={-3.7} y2={0.9} />
    </g>
  </g>
)

export const TerritoryBands = ({ shapes = [], k = 1, activeKeys, selectedKey, onSelect }) => {
  const reveal = useReveal(shapes.length > 0)
  const ordered = [...shapes].sort((a, b) => a.domination - b.domination)
  const hatch = 5 * k
  return (
    <g>
      <defs>
        <pattern id='live-terr-hatch' patternUnits='userSpaceOnUse' width={hatch} height={hatch} patternTransform='rotate(45)'>
          <line x1={0} y1={0} x2={0} y2={hatch} stroke='var(--terr-hatch)' strokeWidth={1.4 * k} />
        </pattern>
      </defs>
      {ordered.map(t => {
        const p = t.domination * reveal
        const dominated = tierOf(t.domination).key === 'dominated'
        const active = activeKeys?.has(t.key)
        const selected = selectedKey === t.key
        const halfW = t.width / 2
        const seed = seedOf(t.key)
        const battle = t.tracks.map((track, i) => conquestOnTrack(track, p, halfW, seed + i * 0.13))
        const polygons = battle.flatMap(b => b.polygons)
        const fronts = battle.flatMap(b => b.fronts)
        const swords = battle.flatMap(b => b.swords)
        const band = { fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' }
        return (
          <g key={t.key} style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); onSelect && onSelect(t.key) }}>
            {/* Niebla: todo el corredor */}
            {t.lines.map((d, i) => (
              <path key={`fog-${i}`} d={d} {...band} stroke='var(--terr-fog)' strokeWidth={t.width} className={active && p <= 0 ? 'live-terr-active' : undefined} />
            ))}
            {t.lines.map((d, i) => (
              <path
                key={`trail-${i}`}
                d={d}
                fill='none'
                stroke={selected ? 'var(--coral)' : 'var(--terr-fog-line)'}
                strokeWidth={(selected ? 1.6 : 1.1) * k}
                strokeDasharray={`${3 * k} ${4 * k}`}
                strokeLinecap='round'
              />
            ))}

            {/* Brillo de territorio dominado */}
            {dominated && t.lines.map((d, i) => (
              <path key={`halo-${i}`} d={d} {...band} stroke='var(--terr-land)' strokeWidth={t.width * 1.9} style={{ strokeOpacity: `calc(var(--terr-halo-opacity) * ${reveal})` }} />
            ))}

            {polygons.length > 0 && (
              <>
                {/* Territorio: el borde grueso abajo y el relleno arriba deja sólo el contorno exterior */}
                <g style={{ opacity: selected ? 'var(--terr-opacity-hi)' : 'var(--terr-opacity)' }} className={active ? 'live-terr-active' : undefined}>
                  {polygons.map((pts, i) => (
                    <polygon key={`edge-${i}`} points={pts} fill='var(--terr-edge)' stroke='var(--terr-edge)' strokeWidth={2.6 * k} strokeLinejoin='round' />
                  ))}
                  {polygons.map((pts, i) => (
                    <polygon key={`land-${i}`} points={pts} fill='var(--terr-land)' />
                  ))}
                </g>
                <g style={{ opacity: 'var(--terr-hatch-opacity)' }}>
                  {polygons.map((pts, i) => (
                    <polygon key={`hatch-${i}`} points={pts} fill='url(#live-terr-hatch)' />
                  ))}
                </g>
              </>
            )}

            {/* Frente de batalla */}
            {fronts.map((f, i) => (
              <polyline key={`front-${i}`} points={f.line} fill='none' stroke='var(--terr-front)' strokeWidth={2 * k} strokeLinejoin='round' strokeLinecap='round' className='live-front-pulse' />
            ))}
            {swords.map((s, i) => <Swords key={`swords-${i}`} x={s[0]} y={s[1]} k={k} />)}

            {/* Banderas en las ciudades tomadas */}
            {p > 0 && t.flagPoints.map((f, i) => <Flag key={`flag-${i}`} x={f[0]} y={f[1]} k={k} />)}
          </g>
        )
      })}
    </g>
  )
}

// Las etiquetas se dibujan a escala fija en pantalla (k = 1/zoom) y un poco más
// grandes que las del resto del mapa para leerse de lejos en la TV.
const LABEL_SCALE = 1.3

const Crown = ({ x, color }) => (
  <path transform={`translate(${x}, 0)`} d='M-4.6,2.8 L-4.6,-2.6 L-2.2,-0.4 L0,-3.8 L2.2,-0.4 L4.6,-2.6 L4.6,2.8 Z' fill={color} />
)

// Etiquetas con el % de cada territorio, al costado del corredor para no tapar el frente.
export const TerritoryLabels = ({ shapes = [], k = 1, selectedKey, onSelect }) => {
  const reveal = useReveal(shapes.length > 0)
  return (
    <g>
      {shapes.map(t => {
        const tier = tierOf(t.domination)
        const dominated = tier.key === 'dominated'
        const pct = formatPct(t.domination * reveal)
        const selected = selectedKey === t.key
        // Ancho aproximado del texto (bold ~5.3 por carácter a 8.5px).
        const textW = (t.short.length + 1 + pct.length) * 5.3
        const iconW = dominated ? 12 : 9
        const w = textW + iconW + 14
        const x0 = -w / 2
        const { x, y, nx, ny } = t.labelAnchor
        const gap = t.width * 0.6 + (Math.abs(nx) * (w / 2) + Math.abs(ny) * 9 + 4) * k * LABEL_SCALE
        return (
          <g
            key={`label-${t.key}`}
            transform={`translate(${x + nx * gap}, ${y + ny * gap}) scale(${k * LABEL_SCALE})`}
            style={{ cursor: 'pointer' }}
            onClick={e => { e.stopPropagation(); onSelect && onSelect(t.key) }}
          >
            <rect x={x0} y={-9} width={w} height={18} rx={9} fill='var(--panel)' stroke={selected ? 'var(--coral)' : tier.color} strokeWidth={selected ? 1.6 : 1} />
            {dominated ? <Crown x={x0 + 11} color={tier.color} /> : <circle cx={x0 + 10} cy={0} r={2.8} fill={tier.color} />}
            <text x={x0 + iconW + 8} y={3} fontSize={8.5} fontWeight={700} style={{ fill: 'var(--map-label)' }}>
              {t.short} <tspan fontWeight={800} style={{ fill: tier.color }}>{pct}</tspan>
            </text>
          </g>
        )
      })}
    </g>
  )
}
